import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import messages from '../../commons/constMessages';
import { useRecoilState } from 'recoil';
import { sessionAtom } from '../../atoms/sessionAtom';
import apiUtils from '../../api/apiUtils';
import guardianIdServices from '../../services/guardianIdServices';
import { PulseLoader } from 'react-spinners';
import '../../assets/css/login.css?v=1.0.0';
import './login.css';

// Chaves em sessionStorage: login pendente (PKCE/state/nonce/endpoint) e retorno gravado por public/signin-oidc.html.
const PENDING_KEY = 'gid_login';
const CALLBACK_KEY = 'gid_callback';

const errorMessages = {
  temporario: messages.ErrGidTemporario,
  configuracao: messages.ErrGidConfiguracao,
  login: messages.ErrGidLogin
};

const takeItem = key => {
  const value = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  try {
    return value ? JSON.parse(value) : null;
  } catch (e) {
    return null;
  }
};

const LoginGuardianId = props => {
  const [session, setSession] = useRecoilState(sessionAtom);
  const [isLoading, setIsLoading] = useState(false);
  // Evita um segundo authorize enquanto o retorno do primeiro ainda está sendo tratado.
  const inProgress = useRef(false);

  useEffect(() => {
    if (props.user.client && !props.user.authError && !session.logoff && !inProgress.current) {
      inProgress.current = true;
      initiate()
        .then(redirecting => { if (!redirecting) inProgress.current = false; })
        .catch(() => {
          inProgress.current = false;
          fail(messages.ErrGidTemporario);
        });
    }
  }, [props.user]);

  const fail = message => {
    const localUser = { ...props.user };
    localUser.authError = true;
    props.setUser(localUser);
    props.showErrorMessage(message);
    setIsLoading(false);
  };

  const initiate = async () => {
    setIsLoading(true);
    const config = await apiUtils.getConfig();
    const gidConfig = config && config.guardianId;
    if (!guardianIdServices.checkConfig(gidConfig)) {
      fail(messages.ErrGidConfiguracao);
      return;
    }

    // Sem EndpointName válido no caminho não há login pelo Guardian ID (não existe mais o código digitado).
    const endpoint = guardianIdServices.resolveGidEndpoint(window.location.href);
    if (!endpoint) {
      fail(messages.ErrGidSemEndpoint);
      return;
    }

    const callback = takeItem(CALLBACK_KEY);
    if (callback) {
      const pending = takeItem(PENDING_KEY);
      await completeLogin(gidConfig, endpoint, callback, pending);
      return;
    }

    const pkce = await guardianIdServices.createPkce();
    const { verifier, state, nonce } = pkce;
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ verifier, state, nonce, endpoint }));
    const promptLogin = !!session.gidPromptLogin;
    window.location.assign(guardianIdServices.buildAuthorizeUrl(gidConfig, endpoint, pkce, { promptLogin }));
    return true;
  };

  const completeLogin = async (gidConfig, endpoint, callback, pending) => {
    const parsed = guardianIdServices.parseCallback(callback, pending && pending.endpoint === endpoint ? pending : null);
    if (!parsed.ok) {
      fail(errorMessages[parsed.kind]);
      return;
    }

    const exchanged = await guardianIdServices.exchangeCode(gidConfig, parsed.code, pending.verifier);
    if (!exchanged.ok) {
      fail(errorMessages[exchanged.kind]);
      return;
    }

    const validated = guardianIdServices.validateTokens(exchanged.tokens, pending, props.user.client.id);
    if (!validated.ok) {
      fail(errorMessages[validated.kind]);
      return;
    }

    const localUser = { ...props.user };
    localUser.password = '';
    localUser.authError = false;
    localUser.userName = validated.userName;
    localUser.name = validated.userName;
    localUser.accessInfo = { accessToken: validated.accessToken, expiresIn: validated.expiresIn };
    localUser.isAuth = true;
    setSession(guardianIdServices.sessionAfterLogin(session));
    props.setUser(localUser);
  };

  const getComponent = () => {
    const component =
      <>
        {props.children}
        {getButton()}
      </>;
    return component;
  };

  const getButton = () => {
    if (isLoading) {
      return <center><PulseLoader size={10} color={'#333'} loading={true} /></center>;
    }
    const btn =
      <button className="btn btn-lg btn-success btn-block" onClick={handleSubmit}>
        Recarregar
      </button>;
    return btn;
  };

  const handleSubmit = e => {
    setIsLoading(true);
    e.preventDefault();

    const localUser = { ...props.user };
    localUser.authError = false;
    props.setUser(localUser);

    setSession(guardianIdServices.sessionForReload(session));
  };

  return (getComponent());
};

LoginGuardianId.propTypes = {
  user: PropTypes.object.isRequired,
  setUser: PropTypes.func.isRequired,
  proceedLogin: PropTypes.func.isRequired,
  showErrorMessage: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  children: PropTypes.node
};

export default LoginGuardianId;
